import React, { useState, useContext, createContext, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, apiUrl } from '../config/api';

// Theme Context
export const ThemeContext = createContext();

const Setari = ({ currentUser, currentUserRole }) => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState(null); // 'clienti' | 'produse'
  const [bulkPassword, setBulkPassword] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    username: '',
    password: '',
    role: 'Office'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [exporting, setExporting] = useState(false);
  const [downloadingLogs, setDownloadingLogs] = useState(false);

  // Fetch users if admin
  useEffect(() => {
    if (currentUserRole === 'Admin') {
      fetchUsers();
    }
  }, [currentUserRole]);

  const fetchUsers = async () => {
    try {
      console.log('Testing backend connection...');
      
      // First test basic connectivity
      try {
        const testResponse = await axios.get(`${API_BASE_URL}/test`);
        console.log('Backend test successful:', testResponse.data);
      } catch (testError) {
        console.error('Backend test failed:', testError);
        setError(`Backend server nu răspunde. Verificați dacă serverul rulează pe portul 5000.`);
        return;
      }

      console.log('Fetching users from:', `${API_BASE_URL}/users`);
      const response = await axios.get(`${API_BASE_URL}/users`);
      console.log('Users response:', response.data);
      setUsers(response.data);
      setError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error config:', error.config);
      
      if (error.response?.status === 404) {
        setError(`Endpoint-ul /api/users nu a fost găsit. Verificați dacă backend-ul este actualizat.`);
      } else {
        setError(`Eroare la încărcarea utilizatorilor: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  // Theme management
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('darkMode', JSON.stringify(newTheme));
    
    // Apply theme to document
    if (newTheme) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
    
    setSuccess('Tema a fost schimbată cu succes!');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Password change functionality
  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('Toate câmpurile sunt obligatorii');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Parolele noi nu se potrivesc');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Parola nouă trebuie să aibă cel puțin 6 caractere');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/change-password`, {
        username: currentUser?.username,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setSuccess('Parola a fost schimbată cu succes!');
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Eroare la schimbarea parolei');
    } finally {
      setLoading(false);
    }
  };

  // Export all data functionality
  const handleExportAllData = async () => {
    setExporting(true);
    setError('');

    try {
      // Fetch all data from all endpoints
      const [clientiRes, produseRes, operatoriRes, statiiRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/clienti`),
        axios.get(`${API_BASE_URL}/produse`),
        axios.get(`${API_BASE_URL}/operatori`),
        axios.get(`${API_BASE_URL}/statii-lucru`)
      ]);

      // Create CSV content for each data type
      const createCSV = (data, headers) => {
        const csvContent = [
          headers.join(','),
          ...data.map(item => headers.map(header => {
            let value = item[header] || '';
            if (typeof value === 'object') {
              value = JSON.stringify(value);
            }
            // Escape commas and quotes
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(','))
        ].join('\n');
        return csvContent;
      };

      // Get workstations for dynamic headers
      const statiiLucru = statiiRes.data;
      const produseHeaders = ['denumire', 'neckSize', 'culoare', 'createdBy'];
      
      // Add dynamic workstation headers
      statiiLucru.forEach(statie => {
        produseHeaders.push(`statie_${statie.id}_${statie.denumire.replace(/[^a-zA-Z0-9]/g, '_')}`);
      });

      // Prepare produse data with flattened workstation values
      const produseData = produseRes.data.map(produs => {
        const flatProduct = {
          denumire: produs.denumire,
          neckSize: produs.neckSize,
          culoare: produs.culoare,
          createdBy: produs.createdBy
        };
        
        // Add workstation values
        statiiLucru.forEach(statie => {
          const key = `statie_${statie.id}_${statie.denumire.replace(/[^a-zA-Z0-9]/g, '_')}`;
          flatProduct[key] = produs.statiiLucru?.[statie.id] || '';
        });
        
        return flatProduct;
      });

      // Create CSV files
      const clientiCSV = createCSV(clientiRes.data, ['nume', 'createdBy']);
      const produseCSV = createCSV(produseData, produseHeaders);
      const operatoriCSV = createCSV(operatoriRes.data, ['nume', 'createdBy']);
      const statiiCSV = createCSV(statiiRes.data, ['denumire', 'intervalFunc', 'sfarsit', 'repaus', 'createdBy']);

      // Create and download zip file using JSZip library (we'll use a simple approach)
      // For now, we'll create separate downloads
      const downloadCSV = (content, filename) => {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      // Download all files
      const dateStr = new Date().toISOString().split('T')[0];
      downloadCSV(clientiCSV, `clienti_${dateStr}.csv`);
      
      setTimeout(() => {
        downloadCSV(produseCSV, `produse_${dateStr}.csv`);
      }, 500);
      
      setTimeout(() => {
        downloadCSV(operatoriCSV, `operatori_${dateStr}.csv`);
      }, 1000);
      
      setTimeout(() => {
        downloadCSV(statiiCSV, `statii_lucru_${dateStr}.csv`);
      }, 1500);

      setSuccess('Export complet realizat cu succes! Au fost descărcate 4 fișiere CSV.');
      setTimeout(() => setSuccess(''), 5000);

    } catch (error) {
      console.error('Export error:', error);
      setError('Eroare la exportarea datelor');
    } finally {
      setExporting(false);
    }
  };

  // Download audit logs (Admin only)
  const handleDownloadLogs = async () => {
    try {
      setDownloadingLogs(true);
      setError('');
      const res = await axios.get(`${API_BASE_URL}/admin/logs/download`, {
        responseType: 'blob',
        headers: { 'x-user-role': currentUserRole }
      });
      const blob = new Blob([res.data], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `audit-logs_${dateStr}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('Logurile au fost descărcate.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      console.error('Download logs error:', e);
      setError('Eroare la descărcarea logurilor.');
    } finally {
      setDownloadingLogs(false);
    }
  };

  // User management functions
  const handleCreateUser = () => {
    setEditingUser(null);
    setUserFormData({
      username: '',
      password: '',
      role: 'Office'
    });
    setShowUserModal(true);
    setError('');
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      username: user.username,
      password: '', // Don't pre-fill password for security
      role: user.role
    });
    setShowUserModal(true);
    setError('');
  };

  const handleSaveUser = async () => {
    if (!userFormData.username || (!editingUser && !userFormData.password)) {
      setError('Numele de utilizator și parola sunt obligatorii pentru utilizatori noi');
      return;
    }

    setLoading(true);
    try {
      const userData = {
        ...userFormData,
        currentUserRole: currentUserRole,
        actor: currentUser?.username
      };

      if (editingUser) {
        // Update user
        if (!userFormData.password) {
          delete userData.password; // Don't update password if not provided
        }
        await axios.put(`${API_BASE_URL}/users/${editingUser.id}`, userData);
        setSuccess('Utilizatorul a fost actualizat cu succes!');
      } else {
        // Create user
        await axios.post(`${API_BASE_URL}/users`, userData);
        setSuccess('Utilizatorul a fost creat cu succes!');
      }

      await fetchUsers();
      setShowUserModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving user:', error);
      setError(error.response?.data?.error || 'Eroare la salvarea utilizatorului');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Sigur doriți să ștergeți utilizatorul "${user.username}"?`)) {
      setLoading(true);
      try {
        await axios.delete(`${API_BASE_URL}/users/${user.id}`, {
          data: { currentUserRole: currentUserRole, actor: currentUser?.username }
        });
        setSuccess('Utilizatorul a fost șters cu succes!');
        await fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error deleting user:', error);
        setError(error.response?.data?.error || 'Eroare la ștergerea utilizatorului');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ 
      backgroundColor: isDarkMode ? '#2d3748' : 'white',
      color: isDarkMode ? 'white' : 'black',
      minHeight: '100vh',
      padding: '1rem'
    }}>
  {/* Utilă pentru formatarea sigură a datelor create_at / createdAt */}
  {/** Funcție locală pentru afișare dată (zi.lună.an) sau '-' dacă lipsește */}
  {(() => { /* scurt closure doar pentru a nu polua scope-ul global */ })()}
      <div className="card" style={{ 
        backgroundColor: isDarkMode ? '#4a5568' : 'white',
        color: isDarkMode ? 'white' : 'black'
      }}>
        <h2>⚙️ Setări</h2>
        
        {error && (
          <div style={{ 
            background: '#f8d7da', 
            color: '#721c24', 
            padding: '0.75rem', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ 
            background: '#d4edda', 
            color: '#155724', 
            padding: '0.75rem', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}

        {/* Theme Toggle Section */}
        <div style={{ 
          background: isDarkMode ? '#1e1e1e' : '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: `1px solid ${isDarkMode ? '#2e2e2e' : '#dee2e6'}`
        }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {isDarkMode ? '🌙' : '☀️'} Temă Aplicație
          </h3>
          <p style={{ marginBottom: '1rem', color: isDarkMode ? '#888888' : '#6c757d' }}>
            Schimbați între tema închisă și tema deschisă pentru o experiență personalizată.
          </p>
          <button 
            className="btn"
            onClick={toggleTheme}
            style={{ 
              background: isDarkMode ? '#333333' : '#007bff',
              color: isDarkMode ? '#e0e0e0' : 'white',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {isDarkMode ? '☀️ Schimbă la tema deschisă' : '🌙 Schimbă la tema închisă'}
          </button>
        </div>

        {/* Password Change Section */}
        <div style={{ 
          background: isDarkMode ? '#1e1e1e' : '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: `1px solid ${isDarkMode ? '#2e2e2e' : '#dee2e6'}`
        }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            🔒 Schimbare Parolă
          </h3>
          <p style={{ marginBottom: '1rem', color: isDarkMode ? '#888888' : '#6c757d' }}>
            Schimbați parola contului dvs. pentru o securitate sporită.
          </p>
          <button 
            className="btn btn-warning"
            onClick={() => setShowPasswordModal(true)}
            style={{ 
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🔑 Schimbă Parola
          </button>
        </div>

        {/* Export Data Section */}
        <div style={{ 
          background: isDarkMode ? '#1e1e1e' : '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: `1px solid ${isDarkMode ? '#2e2e2e' : '#dee2e6'}`
        }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            📦 Export Date Complete
          </h3>
          <p style={{ marginBottom: '1rem', color: isDarkMode ? '#888888' : '#6c757d' }}>
            Exportați toate datele din aplicație (Produse, Operatori, Stații de lucru) în format CSV.
          </p>
          <button 
            className="btn btn-success"
            onClick={handleExportAllData}
            disabled={exporting}
            style={{ 
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: exporting ? 0.6 : 1
            }}
          >
            {exporting ? '📤 Exportă...' : '📤 Exportă Toate Datele'}
          </button>
        </div>

        {/* User Management Section - Admin Only */}
        {currentUserRole === 'Admin' && (
          <div style={{ 
            background: isDarkMode ? '#1e1e1e' : '#f8f9fa', 
            padding: '1.5rem', 
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: `1px solid ${isDarkMode ? '#2e2e2e' : '#dee2e6'}`
          }}>
            <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              👥 Gestionare Utilizatori
            </h3>
            <p style={{ marginBottom: '1rem', color: isDarkMode ? '#888888' : '#6c757d' }}>
              Creați, editați și ștergeți utilizatori. Schimbați rolurile și parolele utilizatorilor.
            </p>
            
            <div style={{ marginBottom: '1rem' }}>
              <button 
                className="btn btn-primary"
                onClick={handleCreateUser}
                style={{ 
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}
              >
                ➕ Adaugă Utilizator Nou
              </button>
            </div>

            {/* Users Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: isDarkMode ? '#181818' : 'white',
                border: `1px solid ${isDarkMode ? '#2e2e2e' : '#dee2e6'}`
              }}>
                <thead>
                  <tr style={{ backgroundColor: isDarkMode ? '#111111' : '#f8f9fa' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                      Nume Utilizator
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                      Rol
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                      Creat La
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                      Acțiuni
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '0.75rem' }}>{user.username}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{ 
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          backgroundColor: 
                            user.role === 'Admin' ? '#dc3545' : 
                            user.role === 'Office' ? '#007bff' : '#28a745',
                          color: 'white'
                        }}>
                          {user.role}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {(() => {
                          const raw = user.createdAt || user.created_at;
                          if(!raw) return '-';
                          const d = new Date(raw);
                          if(isNaN(d.getTime())) return '-';
                          return d.toLocaleDateString('ro-RO', { year:'numeric', month:'2-digit', day:'2-digit' });
                        })()}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            className="btn btn-warning"
                            onClick={() => handleEditUser(user)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                          >
                            ✏️ Editează
                          </button>
                          {user.username !== 'admin' && (
                            <button
                              className="btn btn-danger"
                              onClick={() => handleDeleteUser(user)}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                            >
                              🗑️ Șterge
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Audit & Loguri - Admin only */}
        {currentUserRole === 'Admin' && (
          <div style={{ 
            background: isDarkMode ? '#1e1e1e' : '#f8f9fa', 
            padding: '1.5rem', 
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: `1px solid ${isDarkMode ? '#2e2e2e' : '#dee2e6'}`
          }}>
            <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              🧾 Audit & Loguri
            </h3>
            <p style={{ marginBottom: '1rem', color: isDarkMode ? '#888888' : '#6c757d' }}>
              Descărcați toate logurile aplicației (acțiuni create/editate/salvate/șterse, cu dată și oră), în ordine cronologică.
            </p>
            <button 
              className="btn btn-secondary"
              onClick={handleDownloadLogs}
              disabled={downloadingLogs}
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: downloadingLogs ? 0.6 : 1
              }}
            >
              {downloadingLogs ? '⬇️ Se descarcă...' : '⬇️ Descarcă loguri'}
            </button>
          </div>
        )}

        {/* Admin Dangerous Operations */}
        {currentUserRole === 'Admin' && (
          <div style={{ 
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '2px dashed ' + (isDarkMode ? '#cbd5e0' : '#dee2e6')
          }}>
            <h4 style={{ marginBottom: '0.75rem' }}>⚠️ Operațiuni Administrative</h4>
            <p style={{ marginBottom: '0.75rem', color: isDarkMode ? '#2d3748' : '#6c757d' }}>
              Atenție: aceste acțiuni sunt ireversibile. Vor șterge toate înregistrările selectate.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                className="btn btn-danger"
                onClick={() => { setBulkDeleteType('clienti'); setShowBulkDeleteModal(true); setBulkPassword(''); setError(''); }}
                style={{ padding: '0.6rem 1rem' }}
              >
                🗑️ Șterge Toți Clienții
              </button>
              <button
                className="btn btn-danger"
                onClick={() => { setBulkDeleteType('produse'); setShowBulkDeleteModal(true); setBulkPassword(''); setError(''); }}
                style={{ padding: '0.6rem 1rem' }}
              >
                🗑️ Șterge Toate Produsele
              </button>
            </div>
          </div>
        )}

        {/* User Info */}
        <div style={{ 
          marginTop: '2rem',
          padding: '1rem',
          background: isDarkMode ? '#1e1e1e' : '#e9ecef',
          borderRadius: '8px',
          border: isDarkMode ? '1px solid #2e2e2e' : 'none',
          fontSize: '0.9rem',
          color: isDarkMode ? '#888888' : '#6c757d'
        }}>
          <strong>Utilizator conectat:</strong> {currentUser?.username || 'Necunoscut'}
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: isDarkMode ? '#4a5568' : 'white',
            color: isDarkMode ? 'white' : 'black',
            padding: '2rem',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '450px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            border: `2px solid ${isDarkMode ? '#68d391' : '#007bff'}`,
            margin: 'auto'
          }}>
            <h3 style={{ 
              marginBottom: '1.5rem', 
              textAlign: 'center',
              color: isDarkMode ? '#68d391' : '#007bff',
              fontSize: '1.5rem'
            }}>
              🔒 Schimbare Parolă
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 'bold',
                textAlign: 'left'
              }}>
                Parola Curentă:
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: `1px solid ${isDarkMode ? '#68d391' : '#ced4da'}`,
                  fontSize: '1rem',
                  backgroundColor: isDarkMode ? '#2d3748' : 'white',
                  color: isDarkMode ? 'white' : 'black',
                  boxSizing: 'border-box'
                }}
                placeholder="Introduceți parola curentă"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 'bold',
                textAlign: 'left'
              }}>
                Parola Nouă:
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: `1px solid ${isDarkMode ? '#68d391' : '#ced4da'}`,
                  fontSize: '1rem',
                  backgroundColor: isDarkMode ? '#2d3748' : 'white',
                  color: isDarkMode ? 'white' : 'black',
                  boxSizing: 'border-box'
                }}
                placeholder="Introduceți parola nouă"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 'bold',
                textAlign: 'left'
              }}>
                Confirmă Parola Nouă:
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: `1px solid ${isDarkMode ? '#68d391' : '#ced4da'}`,
                  fontSize: '1rem',
                  backgroundColor: isDarkMode ? '#2d3748' : 'white',
                  color: isDarkMode ? 'white' : 'black',
                  boxSizing: 'border-box'
                }}
                placeholder="Confirmați parola nouă"
              />
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setError('');
                }}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  minWidth: '120px'
                }}
              >
                Anulează
              </button>
              <button
                className="btn btn-primary"
                onClick={handlePasswordChange}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  minWidth: '140px'
                }}
              >
                {loading ? 'Se schimbă...' : 'Schimbă Parola'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      {showUserModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: isDarkMode ? '#4a5568' : 'white',
            color: isDarkMode ? 'white' : 'black',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              {editingUser ? '✏️ Editează Utilizator' : '➕ Adaugă Utilizator Nou'}
            </h3>

            {error && (
              <div style={{ 
                background: '#f8d7da', 
                color: '#721c24', 
                padding: '0.75rem', 
                borderRadius: '4px', 
                marginBottom: '1rem',
                border: '1px solid #f5c6cb'
              }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Nume Utilizator:
              </label>
              <input
                type="text"
                value={userFormData.username}
                onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  fontSize: '1rem',
                  backgroundColor: isDarkMode ? '#2d3748' : 'white',
                  color: isDarkMode ? 'white' : 'black'
                }}
                placeholder="Introduceți numele de utilizator"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                {editingUser ? 'Parolă Nouă (opțional):' : 'Parolă:'}
              </label>
              <input
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  fontSize: '1rem',
                  backgroundColor: isDarkMode ? '#2d3748' : 'white',
                  color: isDarkMode ? 'white' : 'black'
                }}
                placeholder={editingUser ? 'Lăsați gol pentru a păstra parola actuală' : 'Introduceți parola'}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Rol:
              </label>
              <select
                value={userFormData.role}
                onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  fontSize: '1rem',
                  backgroundColor: isDarkMode ? '#2d3748' : 'white',
                  color: isDarkMode ? 'white' : 'black'
                }}
              >
                <option value="Admin">Admin - Acces complet + gestionare utilizatori</option>
                <option value="Office">Office - Acces complet la toate paginile</option>
                <option value="Operatori">Operatori - Acces limitat (Calendar)</option>
              </select>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end',
              borderTop: '1px solid #dee2e6',
              paddingTop: '1rem'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowUserModal(false);
                  setError('');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  minWidth: '120px'
                }}
              >
                Anulează
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveUser}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  minWidth: '140px'
                }}
              >
                {loading ? 'Se salvează...' : (editingUser ? 'Actualizează' : 'Creează')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal (Admin) */}
      {showBulkDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: isDarkMode ? '#4a5568' : 'white',
            color: isDarkMode ? 'white' : 'black',
            padding: '1.5rem',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '480px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ marginBottom: '0.75rem', color: '#dc3545', textAlign: 'center' }}>
              Confirmare Ștergere în Masă
            </h3>
            <p style={{ marginBottom: '0.75rem', textAlign: 'center' }}>
              Sunteți pe cale să ștergeți <strong>toate {bulkDeleteType === 'clienti' ? 'înregistrările de clienți' : 'înregistrările de produse'}</strong>.
              Această acțiune este ireversibilă.
            </p>
            <p style={{ margin: '0 0 0.5rem 0', textAlign: 'center' }}>
              Pentru confirmare, introduceți parola pentru utilizatorul administrator <strong>{currentUser?.username}</strong>.
            </p>
            <input
              type="password"
              value={bulkPassword}
              onChange={(e) => setBulkPassword(e.target.value)}
              placeholder="Parola administratorului"
              style={{
                width: '70%',
                maxWidth: '360px',
                padding: '0.75rem',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '1rem',
                margin: '0.75rem auto 1rem auto',
                display: 'block',
                backgroundColor: isDarkMode ? '#2d3748' : 'white',
                color: isDarkMode ? 'white' : 'black'
              }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowBulkDeleteModal(false); setBulkPassword(''); setBulkDeleteType(null); setError(''); }}
              >
                Anulează
              </button>
              <button
                className="btn btn-danger"
                disabled={!bulkPassword || loading}
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  try {
                    const endpoint = bulkDeleteType === 'clienti' ? 'delete-all-clienti' : 'delete-all-produse';
                    const res = await axios.post(`${API_BASE_URL}/admin/${endpoint}`, {
                      username: currentUser?.username,
                      password: bulkPassword
                    });
                    setSuccess(res.data?.message || 'Operațiunea s-a finalizat cu succes');
                    // Refresh relevant lists via custom events
                    if (bulkDeleteType === 'clienti') {
                      window.dispatchEvent(new Event('refresh-clienti'));
                    } else {
                      window.dispatchEvent(new Event('refresh-produse'));
                    }
                    setShowBulkDeleteModal(false);
                    setBulkPassword('');
                    setBulkDeleteType(null);
                    setTimeout(() => setSuccess(''), 4000);
                  } catch (err) {
                    console.error('Bulk delete error:', err);
                    setError(err.response?.data?.error || 'Eroare la operațiunea de ștergere în masă');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {loading ? 'Se șterge...' : 'Confirmă Ștergerea'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Setari;
